import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  ArchiveRestore,
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  RefreshCw,
  Search,
  ShieldAlert,
  Zap
} from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDateTime } from '../utils/dateFormat';

export default function MasterLogs({ masterLogView = '' }) {
  const [activeTab, setActiveTab] = useState('activity'); // 'activity' | 'driver' | 'delete' | 'errors'
  const [activityLogs, setActivityLogs] = useState([]);
  const [driverLogs, setDriverLogs] = useState([]);
  const [deleteLogs, setDeleteLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'success' | 'failed'

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Sync tab from menu subitem if specified
  useEffect(() => {
    if (masterLogView === 'master-logs-driver') setActiveTab('driver');
    else if (masterLogView === 'master-logs-delete') setActiveTab('delete');
    else if (masterLogView === 'master-logs-errors') setActiveTab('errors');
    else if (masterLogView === 'master-logs-activity') setActiveTab('activity');
  }, [masterLogView]);

  const fetchAllLogs = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch Activity Logs
      const actPromise = axios.get(`${API_URL}/api/reports/activity-history?limit=300`, { headers: getHeaders() })
        .catch(() => ({ data: [] }));

      // 2. Fetch Driver Assignment Logs
      const driverPromise = axios.get(`${API_URL}/api/profile/driver-logs`, { headers: getHeaders() })
        .catch(() => ({ data: [] }));

      // 3. Fetch Delete History Logs
      const delPromise = axios.get(`${API_URL}/api/delete-history?limit=300`, { headers: getHeaders() })
        .catch(() => ({ data: [] }));

      const [actRes, driverRes, delRes] = await Promise.all([actPromise, driverPromise, delPromise]);

      setActivityLogs(actRes.data || []);
      setDriverLogs(driverRes.data || []);
      setDeleteLogs(delRes.data || []);
    } catch (err) {
      console.error('Failed to load system master logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllLogs();
  }, [fetchAllLogs]);

  // Auto-refresh interval (every 15s when enabled)
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchAllLogs();
    }, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAllLogs]);

  // Filtered Activity Logs
  const filteredActivityLogs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return activityLogs.filter((item) => {
      const matchSearch = !term || [
        item.date,
        item.workspace,
        item.user,
        item.role,
        item.module,
        item.action,
        item.description,
        item.complete_details,
        item.path,
        item.status
      ].filter(Boolean).join(' ').toLowerCase().includes(term);

      const isFailed = Number(item.status) >= 400;
      const matchStatus = statusFilter === 'all' ||
        (statusFilter === 'failed' && isFailed) ||
        (statusFilter === 'success' && !isFailed);

      return matchSearch && matchStatus;
    });
  }, [activityLogs, search, statusFilter]);

  // Filtered Driver Logs
  const filteredDriverLogs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return driverLogs;
    return driverLogs.filter((item) => [
      item.vehicle_no,
      item.driver_name,
      item.assigned_by,
      item.assigned_at
    ].filter(Boolean).join(' ').toLowerCase().includes(term));
  }, [driverLogs, search]);

  // Filtered Delete Logs
  const filteredDeleteLogs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return deleteLogs;
    return deleteLogs.filter((item) => [
      item.entity_type,
      item.entity_id,
      item.entity_label,
      item.deleted_by_name,
      item.deleted_at,
      item.status
    ].filter(Boolean).join(' ').toLowerCase().includes(term));
  }, [deleteLogs, search]);

  // Filtered Error Logs (Activity with status >= 400)
  const filteredErrorLogs = useMemo(() => {
    const term = search.trim().toLowerCase();
    const errorLogs = activityLogs.filter((item) => Number(item.status) >= 400);
    if (!term) return errorLogs;
    return errorLogs.filter((item) => [
      item.date,
      item.workspace,
      item.user,
      item.module,
      item.action,
      item.description,
      item.path,
      item.status
    ].filter(Boolean).join(' ').toLowerCase().includes(term));
  }, [activityLogs, search]);

  const errorCount = useMemo(() => activityLogs.filter((item) => Number(item.status) >= 400).length, [activityLogs]);
  const successCount = useMemo(() => activityLogs.filter((item) => Number(item.status) < 400).length, [activityLogs]);

  const downloadMasterLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/activity-history/download`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `System_Master_Logs_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download master log report');
    }
  };

  return (
    <div className="fixed-panel-page" style={{ padding: '20px' }}>
      {/* HEADER */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        flexWrap: 'wrap',
        marginBottom: '20px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #0284c7, #0369a1)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(2, 132, 199, 0.3)'
            }}>
              <ShieldAlert size={20} />
            </div>
            <h2 style={{ color: '#0f172a', margin: 0, fontSize: '20px', fontWeight: 900 }}>
              System Master Logs & Audit Center
            </h2>
          </div>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '13px' }}>
            SuperAdmin central forensic audit — Activity, Driver Assignments, Deleted Archives & Server Errors across all workspaces.
          </p>
        </div>

        {/* TOP ACTIONS */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Live Auto-Refresh Toggle */}
          <button
            type="button"
            onClick={() => setAutoRefresh(!autoRefresh)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1.5px solid',
              borderColor: autoRefresh ? '#22c55e' : '#cbd5e1',
              background: autoRefresh ? '#f0fdf4' : '#ffffff',
              color: autoRefresh ? '#16a34a' : '#64748b',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            <Zap size={14} color={autoRefresh ? '#16a34a' : '#94a3b8'} />
            {autoRefresh ? 'Live Stream ON (15s)' : 'Live Stream OFF'}
          </button>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={fetchAllLogs}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#334155',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer'
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Refreshing...' : 'Refresh Logs'}
          </button>

          {/* Download Full Audit Excel */}
          <button
            type="button"
            onClick={downloadMasterLogs}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #0284c7, #0369a1)',
              color: '#ffffff',
              fontSize: '12.5px',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(2, 132, 199, 0.25)'
            }}
          >
            <Download size={14} /> Export Audit Log
          </button>
        </div>
      </div>

      {/* STATS OVERVIEW CARDS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          padding: '12px 16px',
          background: '#ffffff',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
            Total Activity Logs
          </div>
          <div style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginTop: '2px' }}>
            {activityLogs.length}
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          background: '#f0fdf4',
          borderRadius: '10px',
          border: '1px solid #bbf7d0',
          boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', color: '#15803d', fontWeight: 700, textTransform: 'uppercase' }}>
            Success Operations
          </div>
          <div style={{ fontSize: '20px', fontWeight: 900, color: '#16a34a', marginTop: '2px' }}>
            {successCount}
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          background: errorCount > 0 ? '#fef2f2' : '#f8fafc',
          borderRadius: '10px',
          border: `1px solid ${errorCount > 0 ? '#fecaca' : '#e2e8f0'}`,
          boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', color: errorCount > 0 ? '#b91c1c' : '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
            Server / API Errors
          </div>
          <div style={{ fontSize: '20px', fontWeight: 900, color: errorCount > 0 ? '#dc2626' : '#64748b', marginTop: '2px' }}>
            {errorCount}
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          background: '#f0f9ff',
          borderRadius: '10px',
          border: '1px solid #bae6fd',
          boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: 700, textTransform: 'uppercase' }}>
            Driver Assignment Logs
          </div>
          <div style={{ fontSize: '20px', fontWeight: 900, color: '#0284c7', marginTop: '2px' }}>
            {driverLogs.length}
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          background: '#fdf4ff',
          borderRadius: '10px',
          border: '1px solid #f5d0fe',
          boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', color: '#86198f', fontWeight: 700, textTransform: 'uppercase' }}>
            Deleted Archives
          </div>
          <div style={{ fontSize: '20px', fontWeight: 900, color: '#a21caf', marginTop: '2px' }}>
            {deleteLogs.length}
          </div>
        </div>
      </div>

      {/* TAB SELECTOR & SEARCH BAR */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '14px',
        flexWrap: 'wrap',
        marginBottom: '16px',
        background: '#ffffff',
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
      }}>
        {/* TABS */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setActiveTab('activity')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '12.5px',
              fontWeight: 800,
              cursor: 'pointer',
              background: activeTab === 'activity' ? '#0284c7' : '#f1f5f9',
              color: activeTab === 'activity' ? '#ffffff' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <Activity size={14} /> All Activity ({activityLogs.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('driver')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '12.5px',
              fontWeight: 800,
              cursor: 'pointer',
              background: activeTab === 'driver' ? '#0284c7' : '#f1f5f9',
              color: activeTab === 'driver' ? '#ffffff' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <Car size={14} /> Driver Logs ({driverLogs.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('delete')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '12.5px',
              fontWeight: 800,
              cursor: 'pointer',
              background: activeTab === 'delete' ? '#0284c7' : '#f1f5f9',
              color: activeTab === 'delete' ? '#ffffff' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <ArchiveRestore size={14} /> Deleted Archive ({deleteLogs.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('errors')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '12.5px',
              fontWeight: 800,
              cursor: 'pointer',
              background: activeTab === 'errors' ? '#dc2626' : '#f1f5f9',
              color: activeTab === 'errors' ? '#ffffff' : (errorCount > 0 ? '#dc2626' : '#64748b'),
              transition: 'all 0.15s ease'
            }}
          >
            <AlertTriangle size={14} /> Server Errors ({errorCount})
          </button>
        </div>

        {/* SEARCH & FILTERS */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1', maxWidth: '480px', minWidth: '240px' }}>
          {activeTab === 'activity' && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '7px 10px',
                borderRadius: '8px',
                border: '1.5px solid #cbd5e1',
                background: '#ffffff',
                fontSize: '12px',
                fontWeight: 700,
                color: '#334155',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Status</option>
              <option value="success">Success Only</option>
              <option value="failed">Errors / Failed</option>
            </select>
          )}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#f8fafc',
            border: '1.5px solid #cbd5e1',
            borderRadius: '8px',
            padding: '7px 12px',
            width: '100%'
          }}>
            <Search size={14} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search user, vehicle, action, IP, module..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                border: 'none',
                background: 'transparent',
                outline: 'none',
                fontSize: '12.5px',
                width: '100%',
                color: '#0f172a'
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', fontSize: '12px' }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* LOG DATA TABLE / TIMELINE */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
      }}>
        {/* 1. ACTIVITY LOGS TAB */}
        {activeTab === 'activity' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
                  <th style={{ padding: '12px 16px' }}>Timestamp</th>
                  <th style={{ padding: '12px 16px' }}>Workspace</th>
                  <th style={{ padding: '12px 16px' }}>User & Role</th>
                  <th style={{ padding: '12px 16px' }}>Module & Action</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Path / Target</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                      Loading activity logs...
                    </td>
                  </tr>
                ) : filteredActivityLogs.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                      No activity logs match your search.
                    </td>
                  </tr>
                ) : (
                  filteredActivityLogs.map((log) => {
                    const isError = Number(log.status) >= 400;
                    const isExpanded = expandedLogId === log.id;
                    return (
                      <React.Fragment key={log.id}>
                        <tr
                          onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            background: isExpanded ? '#f0f9ff' : (isError ? '#fff5f5' : 'transparent'),
                            transition: 'background 0.15s ease'
                          }}
                        >
                          <td style={{ padding: '11px 16px', whiteSpace: 'nowrap', color: '#64748b', fontWeight: 600 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <Clock size={12} color="#0284c7" />
                              {log.date}
                            </div>
                          </td>
                          <td style={{ padding: '11px 16px', fontWeight: 700, color: '#1e293b' }}>
                            {log.workspace}
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <strong style={{ color: '#0f172a' }}>{log.user}</strong>
                            <span style={{
                              display: 'inline-block',
                              marginLeft: '6px',
                              padding: '1px 6px',
                              background: '#e2e8f0',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 700,
                              color: '#475569'
                            }}>
                              {log.role}
                            </span>
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <span style={{
                              padding: '2px 7px',
                              borderRadius: '5px',
                              background: '#e0f2fe',
                              color: '#0369a1',
                              fontWeight: 700,
                              fontSize: '11px'
                            }}>
                              {log.module}
                            </span>
                            <span style={{ marginLeft: '6px', color: '#334155' }}>
                              {log.action}
                            </span>
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '5px',
                              fontWeight: 800,
                              fontSize: '11px',
                              background: isError ? '#fee2e2' : '#dcfce7',
                              color: isError ? '#b91c1c' : '#15803d'
                            }}>
                              {log.status || '200'}
                            </span>
                          </td>
                          <td style={{ padding: '11px 16px', fontFamily: 'monospace', fontSize: '11.5px', color: '#64748b' }}>
                            {log.method} {log.path || '-'}
                          </td>
                          <td style={{ padding: '11px 16px', textAlign: 'right', color: '#0284c7' }}>
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                            <td colSpan="7" style={{ padding: '14px 20px' }}>
                              <div style={{ fontSize: '12px', color: '#334155', lineHeight: '1.6' }}>
                                <strong style={{ color: '#0f172a' }}>Description:</strong> {log.description || 'No additional description'}
                                {log.complete_details && (
                                  <div style={{ marginTop: '6px', color: '#64748b' }}>
                                    <strong>Details:</strong> {log.complete_details}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 2. DRIVER LOGS TAB */}
        {activeTab === 'driver' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
                  <th style={{ padding: '12px 16px' }}>Vehicle Plate</th>
                  <th style={{ padding: '12px 16px' }}>Assigned Driver</th>
                  <th style={{ padding: '12px 16px' }}>Assigned By</th>
                  <th style={{ padding: '12px 16px' }}>Customer ID</th>
                  <th style={{ padding: '12px 16px' }}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                      Loading driver assignment logs...
                    </td>
                  </tr>
                ) : filteredDriverLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                      No driver assignment records found.
                    </td>
                  </tr>
                ) : (
                  filteredDriverLogs.map((log, idx) => (
                    <tr key={log.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '3px 8px',
                          background: '#1e293b',
                          color: '#ffffff',
                          borderRadius: '5px',
                          fontWeight: 800,
                          fontSize: '12px'
                        }}>
                          {log.vehicle_no}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a' }}>
                        {log.driver_name}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>
                        <span style={{
                          padding: '2px 7px',
                          background: '#e0f2fe',
                          color: '#0369a1',
                          borderRadius: '4px',
                          fontWeight: 700,
                          fontSize: '11px'
                        }}>
                          {log.assigned_by || 'Customer'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }}>
                        #{log.customer_id || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Clock size={12} color="#0284c7" />
                          {new Date(log.assigned_at).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 3. DELETED ARCHIVE TAB */}
        {activeTab === 'delete' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
                  <th style={{ padding: '12px 16px' }}>Entity Type</th>
                  <th style={{ padding: '12px 16px' }}>Record Info</th>
                  <th style={{ padding: '12px 16px' }}>Deleted By</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Deleted Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                      Loading deleted archive logs...
                    </td>
                  </tr>
                ) : filteredDeleteLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                      No deleted records in archive.
                    </td>
                  </tr>
                ) : (
                  filteredDeleteLogs.map((log, idx) => (
                    <tr key={log.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '3px 8px',
                          background: '#fdf4ff',
                          color: '#86198f',
                          borderRadius: '5px',
                          fontWeight: 800,
                          fontSize: '11.5px',
                          textTransform: 'capitalize'
                        }}>
                          {String(log.entity_type || 'record').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a' }}>
                        {log.entity_label || `#${log.entity_id}`}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>
                        {log.deleted_by_name || 'Admin'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '2px 7px',
                          borderRadius: '4px',
                          fontWeight: 800,
                          fontSize: '11px',
                          background: log.status === 'restored' ? '#dcfce7' : '#fee2e2',
                          color: log.status === 'restored' ? '#15803d' : '#b91c1c'
                        }}>
                          {log.status || 'deleted'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {log.deleted_at ? formatDateTime(log.deleted_at) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 4. SERVER ERRORS TAB */}
        {activeTab === 'errors' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#fef2f2', borderBottom: '1.5px solid #fecaca', color: '#991b1b', fontWeight: 800 }}>
                  <th style={{ padding: '12px 16px' }}>Timestamp</th>
                  <th style={{ padding: '12px 16px' }}>Workspace</th>
                  <th style={{ padding: '12px 16px' }}>User</th>
                  <th style={{ padding: '12px 16px' }}>Error Code</th>
                  <th style={{ padding: '12px 16px' }}>API Endpoint</th>
                  <th style={{ padding: '12px 16px' }}>Error Description</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                      Loading error logs...
                    </td>
                  </tr>
                ) : filteredErrorLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#16a34a' }}>
                      <CheckCircle2 size={32} style={{ margin: '0 auto 8px', display: 'block' }} />
                      <strong>Zero Server Errors! All systems operating smoothly.</strong>
                    </td>
                  </tr>
                ) : (
                  filteredErrorLogs.map((log, idx) => (
                    <tr key={log.id || idx} style={{ borderBottom: '1px solid #fee2e2', background: '#fffafa' }}>
                      <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {log.date}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1e293b' }}>
                        {log.workspace}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#0f172a' }}>
                        {log.user}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: '#dc2626',
                          color: '#ffffff',
                          fontWeight: 800,
                          fontSize: '11px'
                        }}>
                          {log.status || '500'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontFamily: 'monospace', color: '#b91c1c', fontSize: '11.5px' }}>
                        {log.method} {log.path}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#334155' }}>
                        {log.description || log.complete_details || 'Request failure'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
